import { keyIcon } from "../../core/elements/icons/index.js";
import accountingDocument from "../shared/accountingDocument.js";
import getKeyFigureService from "../../core/services/getKeyFigureService.js";
import KeyFigurePresetService from "../../core/services/KeyFigurePresetService.js";
import KeyFigurePresetSettingsWidget from "../../core/widgets/keyFigures/KeyFigurePresetSettingsWidget/KeyFigurePresetSettingsWidget.js";
import notifier from "../../core/widgets/notifier/notifier.js";
import OldSpinnerWidget from "../../deprecated/OldSpinnerWidget/OldSpinnerWidget.js"; // eslint-disable-line import-x/no-deprecated -- todo: migrate to new spinner
import polyglot, { _ } from "../../core/polyglot/polyglot.js";
import regionWidget from "../../core/widgets/regionWidget.js";
import session from "../../core/session.js";

/**
 * `editKeyFigurePresetDocument` is the document used to edit the
 * keyFigure preset provided in the URL.
 */
const editKeyFigurePresetDocument = accountingDocument.subclass((that, my) => {
	my.initialize = function () {
		my.super(...arguments);
		that.documentId = "edit-preset";
	};

	that.title = regionWidget({
		content: (html) => {
			if (my.keyFigurePreset) {
				const icon = my.keyFigurePreset.isSystemPreset()
					? keyIcon
					: () => {};

				html.div(
					{ klass: "title" },
					icon,
					_("Edit {presetName}", {
						presetName: polyglot.translateServerString(
							my.keyFigurePreset.getName(),
						),
					}),
				);
			} else {
				html.render(_("Edit Kpi Preset"));
			}
		},
	});

	that.help = {
		summary: _(
			"This page allows the user to modify and create KPI presets.",
		),
		sections: [
			{
				title: _("Preset Info"),
				content: (html) => {
					html.p(
						_(
							"Gives the user the possibility to set the preset name and description.",
						),
					);
				},
			},
			{
				title: _("Preset KPIs"),
				content: (html) => {
					html.p(
						_(
							"Add or remove KPIs from the currently edited preset.",
						),
					);
				},
			},
		],
	};

	that.hasActionsMenu = function () {
		return false;
	};

	that.hasPeriodPicker = function () {
		return false;
	};

	my.keyFigurePresetService = new KeyFigurePresetService({
		companyId: session.getCompanyId(),
		keyFigureService: getKeyFigureService(),
	});

	my.keyFigureSettingsWidget = new KeyFigurePresetSettingsWidget({
		updateCallback: saveInfo,
		deleteCallback: deletePreset,
	});

	// eslint-disable-next-line import-x/no-deprecated -- todo: migrate to new spinner
	my.spinner = new OldSpinnerWidget({
		size: 64,
		dark: true,
	});

	my.spinner.show();

	that.editPreset = async function (presetId) {
		let visibleKeyFigurePreset;

		try {
			visibleKeyFigurePreset =
				await my.keyFigurePresetService.getVisibleKeyFigures();
		} catch {
			throw _("There should always be a VisibleKeyFigures available");
		}

		try {
			my.keyFigurePreset = await my.keyFigurePresetService.get(presetId);
		} catch {
			my.keyFigurePreset = null;
		}

		my.keyFigureSettingsWidget.setup({
			keyFigurePreset: my.keyFigurePreset,
			allKeyFigures: visibleKeyFigurePreset.getKeyFigures(),
		});

		that.title.update();
		that.update();
	};

	my.renderContentOn = function (html) {
		if (my.keyFigurePreset === undefined) {
			renderEmptyOn(html);
			return;
		}

		if (my.keyFigurePreset === null) {
			renderNoPresetOn(html);
			return;
		}

		html.render(my.section({ content: my.keyFigureSettingsWidget }));
	};

	function renderEmptyOn(html) {
		html.render(my.section({ content: my.spinner }));
	}

	function renderNoPresetOn(html) {
		html.render(
			my.section({
				content: function (html) {
					html.p(
						{ klass: "empty-preset" },
						_(
							"There is no preset matching the URL-provided identifier.",
						),
					);
				},
			}),
		);
	}

	async function saveInfo() {
		await my.keyFigurePresetService.update(my.keyFigurePreset, {
			successMessage: _("KPI preset successfully updated"),
			errorMessage: _("Failed to update {presetName}", {
				presetName: my.keyFigurePreset.getName(),
			}),
		});
	}

	async function deletePreset() {
		try {
			await my.keyFigurePresetService.remove(my.keyFigurePreset.getId());

			if (my.keyFigurePreset.isSystemPreset()) {
				that.editPreset(my.keyFigurePreset.getId());
			} else {
				my.redirectTo("company-settings");
			}
		} catch (error) {
			const response = JSON.parse(error.responseText);
			if (
				response.message &&
				response.message === " - Couch document does not have a rev"
			) {
				notifier.warning(
					_('"{presetName}" was already using its default values', {
						presetName: my.keyFigurePreset.getName(),
					}),
				);

				return;
			}

			notifier.error(
				_('Failed to delete "{presetName}"', {
					presetName: my.keyFigurePreset.getName(),
				}),
			);

			that.update();
		}
	}

	return that;
});

export default editKeyFigurePresetDocument;
